import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ScheduledExportQueue = ({ processingReports, onExport }) => {
  const scheduledReports = [
    {
      id: 1,
      name: 'Daily Revenue Summary',
      schedule: 'Daily at 09:00 WIB',
      nextRun: '2 hours',
      status: 'scheduled',
      recipients: ['finance@company.com', 'cfo@company.com'],
      format: 'Excel'
    },
    {
      id: 2,
      name: 'Fraud Pattern Analysis',
      schedule: 'Every 6 hours',
      nextRun: '45 minutes',
      status: 'scheduled',
      recipients: ['security@company.com'],
      format: 'JSON'
    },
    {
      id: 3,
      name: 'Settlement Reconciliation',
      schedule: 'Daily at 18:00 WIB',
      nextRun: '8 hours',
      status: 'scheduled',
      recipients: ['ops@company.com'],
      format: 'CSV'
    },
    {
      id: 4,
      name: 'Monthly Financial Statement',
      schedule: 'Monthly on 1st',
      nextRun: '5 days',
      status: 'scheduled',
      recipients: ['board@company.com'],
      format: 'PDF'
    }
  ];

  const recentDeliveries = [
    {
      id: 1,
      name: 'Merchant Performance Summary',
      deliveredAt: '15 minutes ago',
      status: 'delivered',
      recipients: 3
    },
    {
      id: 2,
      name: 'Audit Trail Export',
      deliveredAt: '1 hour ago',
      status: 'delivered',
      recipients: 2
    },
    {
      id: 3,
      name: 'Risk Score Distribution',
      deliveredAt: '3 hours ago',
      status: 'failed',
      recipients: 1,
      error: 'SMTP connection timeout'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled':
        return { icon: 'Clock', color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'processing':
        return { icon: 'Loader', color: 'text-yellow-600', bg: 'bg-yellow-100' };
      case 'delivered':
        return { icon: 'CheckCircle', color: 'text-green-600', bg: 'bg-green-100' };
      case 'failed':
        return { icon: 'XCircle', color: 'text-red-600', bg: 'bg-red-100' };
      default:
        return { icon: 'Circle', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  return (
    <div className="space-y-6">
      {/* Processing Reports */}
      {processingReports?.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Loader" size={20} className="text-primary animate-spin" />
            <h3 className="text-lg font-semibold text-foreground">
              Processing Now
            </h3>
          </div>
          <div className="space-y-3">
            {processingReports?.map((report) => (
              <div key={report?.id} className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">
                    {report?.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {Math.round(report?.progress)}%
                  </span>
                </div>
                <div className="w-full bg-background rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${report?.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scheduled Reports */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              Scheduled Exports
            </h3>
          </div>
          <Button variant="ghost" size="sm" iconName="Settings">
            Manage
          </Button>
        </div>
        <div className="space-y-3">
          {scheduledReports?.map((report) => {
            const statusConfig = getStatusIcon(report?.status);
            return (
              <div
                key={report?.id}
                className="p-3 border border-border rounded-lg hover:border-primary/50 transition-all duration-200"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${statusConfig?.bg}`}>
                    <Icon
                      name={statusConfig?.icon}
                      size={16}
                      className={statusConfig?.color}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground mb-1 truncate">
                      {report?.name}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      {report?.schedule}
                    </p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1 text-primary">
                        <Icon name="Clock" size={10} />
                        Next: {report?.nextRun}
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Icon name="FileType" size={10} />
                        {report?.format}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <Icon name="Mail" size={10} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {report?.recipients?.length} recipient(s)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Deliveries */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Send" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">
            Recent Deliveries
          </h3>
        </div>
        <div className="space-y-3">
          {recentDeliveries?.map((delivery) => {
            const statusConfig = getStatusIcon(delivery?.status);
            return (
              <div
                key={delivery?.id}
                className="p-3 border border-border rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${statusConfig?.bg}`}>
                    <Icon
                      name={statusConfig?.icon}
                      size={16}
                      className={statusConfig?.color}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground mb-1 truncate">
                      {delivery?.name}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-1">
                      {delivery?.deliveredAt}
                    </p>
                    {delivery?.status === 'failed' && delivery?.error && (
                      <p className="text-xs text-red-600 mt-1">
                        Error: {delivery?.error}
                      </p>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {delivery?.recipients} recipient(s)
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ScheduledExportQueue;